import { ref, watch, computed } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import router from '@/router/index'

// Notification

export default function useCompletedMyPurchaseRequestList() {
  // Use toast
  const toast = useToast()
  const route = router.history.current

  const refCompletedPurchaseRequestsTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'stringID',
      label: 'Case number',
      sortable: true,
    },
    {
      key: 'receivedDate',
      label: 'PRF Sent on',
      sortable: true,
    },
    {
      key: 'purpose',
      label: 'Justification',
    },
    {
      key: 'requester',
      label: 'Requester',
    },
    {
      key: 'vendor',
      label: 'Vendor',
      sortable: true,
    },
    {
      key: 'company',
      label: 'Entity',
      sortable: true,
    },
    {
      key: 'invoices',
      label: 'Invoice ID',
      sortable: false,
    },
    {
      key: 'assignedTo',
      label: 'Assigned to',
      sortable: true,
    },
    {
      key: 'stage',
      label: 'Stage',
      sortable: true,
    },
    { key: 'status', sortable: true },
    { key: 'actions' },
  ]
  const perPage = ref(route.query.perPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalPurchaseRequests = ref(0)
  const currentPage = ref(route.query.page ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref(route.query.search ?? '')
  const sortBy = ref(route.query.sortBy ?? 'stringID')
  const isSortDirDesc = ref(route.query.sortDesc === 'true')
  const statusFilter = ref([])
  if (Array.isArray(route.query.status)) {
    statusFilter.value = route.query.status
  } else if (route.query.status) {
    statusFilter.value = [route.query.status]
  }
  const stageFilter = ref([])
  if (Array.isArray(route.query.stage)) {
    stageFilter.value = route.query.stage
  } else if (route.query.stage) {
    stageFilter.value = [route.query.stage]
  }
  const invoiceFilter = ref([])
  if (Array.isArray(route.query.invoiceFilter)) {
    invoiceFilter.value = route.query.invoiceFilter
  } else if (route.query.invoiceFilter) {
    invoiceFilter.value = [route.query.invoiceFilter]
  }
  const vendor = ref([])
  if (Array.isArray(route.query.vendor)) {
    vendor.value = route.query.vendor
  } else if (route.query.vendor) {
    vendor.value = [route.query.vendor]
  }
  const entity = ref([])
  if (Array.isArray(route.query.entity)) {
    entity.value = route.query.entity
  } else if (route.query.entity) {
    entity.value = [route.query.entity]
  }
  const requestorFilter = ref([])
  if (Array.isArray(route.query.requestor)) {
    requestorFilter.value = route.query.requestor
  } else if (route.query.requestor) {
    requestorFilter.value = [route.query.requestor]
  }
  const toDeletedID = ref(null)
  const type = ref('completed')
  const purchaseRequestData = ref([])
  const createdAtFilter = ref(route.query.createdAt || '')
  let initialLoad = true

  const dataMeta = computed(() => {
    const localItemsCount = refCompletedPurchaseRequestsTable.value ? refCompletedPurchaseRequestsTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalPurchaseRequests.value,
    }
  })

  const refetchData = () => {
    refCompletedPurchaseRequestsTable.value.refresh()
  }

  watch([searchQuery, statusFilter, invoiceFilter, stageFilter, vendor, entity, createdAtFilter, requestorFilter], () => {
    currentPage.value = 1
    refetchData()
  }, { deep: true })

  watch([currentPage], () => {
    refetchData()
  }, { deep: true })

  const fetchPurchaseRequests = (ctx, callback) => {
    store
      .dispatch('app-completed-my-purchase-request/fetchPurchaseRequests', {
        search: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        status: statusFilter.value,
        stage: stageFilter.value,
        vendor: vendor.value,
        invoiceFilter: invoiceFilter.value,
        entity: entity.value,
        createdAt: createdAtFilter.value,
        requestor: requestorFilter.value,
        type: type.value,
      },
      { root: true })
      .then(response => {
        const { purchaseRequests, total } = response.data
        purchaseRequestData.value = purchaseRequests
        callback(purchaseRequests)
        totalPurchaseRequests.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              status: statusFilter.value,
              stage: stageFilter.value,
              vendor: vendor.value,
              invoiceFilter: invoiceFilter.value,
              entity: entity.value,
              createdAt: createdAtFilter.value,
              requestor: requestorFilter.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const deletePurchaseRequest = () => {
    store
      .dispatch('app-completed-my-purchase-request/deletePurchaseRequest', {
        id: toDeletedID.value,
      },
      { root: true })
      .then(response => {
        refetchData()
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message ?? '',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message ?? process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolvePurchaseRequestStatusVariant = status => {
    if (status === 'verifying') return 'success'
    if (status === 'verified') return 'success'
    if (status === 'draft') return 'secondary'
    if (status === 'pending approval') return 'warning'
    if (status === 'pending edit') return 'warning'
    if (status === 'cancelled') return 'warning'
    return 'primary'
  }

  const resolvePurchaseRequestStageVariant = stage => {
    if (stage === 'in-request') return 'danger'
    if (stage === 'in-quotation') return 'danger'
    if (stage === 'in-order') return 'danger'
    if (stage === 'in-delivery') return 'danger'
    return 'primary'
  }

  return {
    fetchPurchaseRequests,
    tableColumns,
    perPage,
    currentPage,
    totalPurchaseRequests,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refCompletedPurchaseRequestsTable,

    resolvePurchaseRequestStatusVariant,
    resolvePurchaseRequestStageVariant,
    refetchData,

    toDeletedID,
    deletePurchaseRequest,
    purchaseRequestData,

    // Extra Filters
    statusFilter,
    stageFilter,
    invoiceFilter,
    vendor,
    entity,
    createdAtFilter,
    requestorFilter,
  }
}
